/* eslint-disable camelcase */
import _ from 'lodash';
import { lsApiService, lsStorageService } from 'ls-vue-common';
import {
    generalStorageConstants,
    savedOrderModuleConstants,
    savedAddressModuleConstants,
} from '@/services/storage/storage-key.constants';
// eslint-disable-next-line import/no-cycle
import lsTrackService from '@/services/ls-track/ls-track.service';

// actual state management should be handled in the actions that call these methods
export default {
    /**
     * Create a new order
     * @param {Object} orderPayload
     * @return {Promise}
     */
    create(orderPayload) {
        return new Promise((resolve, reject) => {
            lsApiService
                .request({
                    method: 'POST',
                    route: '/orders',
                    payload: orderPayload,
                })
                .then((createdOrder) => {
                    lsTrackService.initiateCheckout({
                        order_id: _.get(createdOrder, 'data.order_id'),
                    });
                    resolve(createdOrder);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    /**
     * Update an order, order must have been created in the last 12 hours
     * @param {Object} order
     * @param {int} order.order_id
     * @return {Promise}
     */
    update(order) {
        return lsApiService.request({
            method: 'PUT',
            route: `/orders/${order.order_id}`,
            payload: order,
        });
    },

    /**
     * If an order_id already exists update order, otherwise create order
     * @param order
     * @return {*|Promise}
     */
    createOrUpdateOrder(order) {
        if (order.order_id) {
            return this.update(order);
        }

        return this.create(order);
    },

    /**
     * Add a priced service to the order's cart
     * @param {Object} order
     * @param {Object} price
     * @param {int} price.price_id
     * @return {Promise}
     */
    addService(order, price) {
        return lsApiService.request({
            method: 'POST',
            route: `/orders/${order.order_id}/services`,
            payload: price,
        });
    },

    /**
     * Add a non-priced service (quote) to the order's cart
     * @param {Object} order
     * @param {int} order.order_id
     * @param {Object} quote
     * @param {int} quote.service_id
     * @return {Promise}
     */
    addQuote(order, quote) {
        return lsApiService.request({
            method: 'POST',
            route: `/orders/${order.order_id}/quotes`,
            payload: quote,
        });
    },

    /**
     * Remove a single service or multiple services from the order's cart by service_ids
     * @param {Object} order
     * @param {int} order.order_id
     * @param {Object|Object[]} services
     * @param {int} services.id
     * @return {Promise}
     */
    removeByServiceIds(order, services) {
        const servicesList = _.isArray(services) ? services : [services];
        return lsApiService.request({
            method: 'POST',
            route: `/orders/${order.order_id}/cart/remove`,
            payload: {
                services: servicesList,
            },
        });
    },

    /**
     * Add an instant quote to the order's cart
     * @param {Object} order
     * @param {int} order.order_id
     * @return {Promise}
     */
    addInstantQuote(order, type) {
        return lsApiService.request({
            method: 'POST',
            route: `/orders/${order.order_id}/instant-quote-${type}`,
        });
    },

    /**
     * Remove an instant quote based on id
     * @param {Object} order
     * @param {int} order.order_id
     * @param {int} instantQuoteId
     * @return {Promise}
     */
    rejectInstantQuote(order, instantQuoteId) {
        return lsApiService.request({
            method: 'POST',
            route: `/orders/${order.order_id}/instant-quotes/${instantQuoteId}/reject`,
            payload: {
                reason: 'signup.removed',
                acceptable_price: 0,
            },
        });
    },

    /**
     * Attempt to add a deal code to the order
     * @param {Number} order_id
     * @param {String} deal
     * @return {Promise}
     */
    addDeal({ order_id, deal }) {
        return lsApiService.request({
            method: 'POST',
            route: `/orders/${order_id}/deal`,
            payload: { deal },
        });
    },

    clearStoredOrderData() {
        lsStorageService.remove(generalStorageConstants.ORDER_CREATED);
        _.forEach(savedOrderModuleConstants, (storageKey) => {
            lsStorageService.remove(storageKey);
        });
        lsStorageService.remove(savedAddressModuleConstants.MODULE);
    },
};
